import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/* shadcn/ui Button, wired to the generated tokens: every colour is a shadcn variable from
 * design/out/shadcn.css, every radius and height a token. No literal value below. */
const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-base font-medium ' +
    // Colors only: `transition-colors` would also fade the focus outline in, and the ring must show at once.
    'transition-[color,background-color,border-color] duration-(--duration-fast) ease-out ' +
    'disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        primary: 'bg-primary text-primary-foreground hover:bg-accent-hover active:bg-accent-press',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-sunken',
        outline: 'border border-input bg-card text-foreground hover:bg-secondary',
        ghost: 'text-ink-secondary hover:bg-secondary hover:text-foreground',
        destructive: 'bg-destructive text-destructive-foreground hover:opacity-90',
        // A grave action's TRIGGER (Close agency, Suspend): rose outline. Solid red stays for the confirm
        // button inside its dialog, so a page never shows two solid red buttons of different weight.
        'danger-outline': 'border border-danger/40 bg-card text-danger hover:bg-urgency-critical-bg hover:text-danger',
      },
      size: {
        // --size-touch-min (44px). Deliberate, not a guess: see docs/design-tokens.md §9.
        default: 'h-touch px-4',
        // Compact rows and toolbars (the invoices page used `h-9 px-3` by hand).
        sm: 'h-9 px-3 text-sm',
        icon: 'size-touch',
      },
    },
    defaultVariants: { variant: 'primary', size: 'default' },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, asChild = false, ...props },
  ref,
) {
  const Comp = asChild ? Slot : 'button';
  return <Comp ref={ref} className={cn(buttonVariants({ variant, size, className }))} {...props} />;
});

export { buttonVariants };
